import React from 'react';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import SolicitacoesAPI from '@/features/prescricao-livre/services/SolicitacoesAPI';
import { PassoExecutado } from '@/models/PassoExecutado';
import { ArtifactType, Procedimento, Solicitacao } from '@/models/Solicitacao';

import { SharedDevices } from '../components/CardSharedDevices/helpers';
import ProcedimentoService from '../services/ProcedimentoService';

type SourceID = PassoExecutado['id'];

export interface SolicitacaoState {
  solicitacao: Solicitacao;
  source: SourceID | null;
  selected: boolean;
  selectable: boolean;
  requested: boolean;
  loading: boolean;
  editing: boolean;
  // Flag to check if item is already saved on the backend
  saved: boolean;
}

export type SolicitacaoUIState = Omit<
  SolicitacaoState,
  'solicitacao' | 'source'
>;

type AddSolicitacaoState = Partial<
  Pick<
    SolicitacaoState,
    'source' | 'selected' | 'selectable' | 'loading' | 'editing'
  >
>;

interface SolicitacoesFilterCriteria {
  tipo: ArtifactType;
  source: SourceID;
}

// Passo is required for request
interface WithSourceCriteria extends Partial<SolicitacoesFilterCriteria> {
  source: SourceID;
}

interface SharedDevicesState {
  source: SourceID | null;
  shared: SharedDevices[];
}

interface ChangeSharedDevices {
  source: SourceID | null;
  shared: SharedDevices[];
}

interface ISolicitacaesContext {
  solicitacoes: Solicitacao[];
  solicitados: Solicitacao[];
  list: (criteria: Partial<SolicitacoesFilterCriteria>) => SolicitacaoState[];
  hasItemsToRequest: (criteria: Partial<SolicitacoesFilterCriteria>) => boolean;

  onAdd: (
    pedido: Solicitacao['pedido'],
    tipo: Solicitacao['tipo'],
    addState?: AddSolicitacaoState,
  ) => void;
  add: (
    solicitacao: Solicitacao,
    source: SourceID,
    resource?: fhir4.FhirResource,
  ) => Promise<void>;
  onEdit: (solicitacao: Solicitacao) => void;
  edit: (solicitacao: Solicitacao, data: fhir4.FhirResource) => Promise<void>;
  change: (
    solicitacao: Solicitacao,
    newState: Partial<SolicitacaoUIState>,
  ) => void;
  changeSharedDevices: (change: ChangeSharedDevices) => void;
  destroy: (solicitacao: Solicitacao) => Promise<void>;
  select: (solicitacao: Solicitacao) => void;
  selection: (solicitacao: Solicitacao[]) => void;
  request: (criteria: WithSourceCriteria) => Promise<void>;
  cancel: (criteria: WithSourceCriteria) => Promise<void>;
}

const SolicitacoesContext = React.createContext<ISolicitacaesContext>({
  solicitacoes: [],
  solicitados: [],
  list: () => [],
  hasItemsToRequest: () => false,
  onAdd: () => undefined,
  add: async () => undefined,
  onEdit: () => undefined,
  edit: async () => undefined,
  change: () => undefined,
  destroy: async () => undefined,
  select: () => undefined,
  selection: () => undefined,
  request: async () => undefined,
  cancel: async () => undefined,
  changeSharedDevices: () => undefined,
});

const solicitacaoStateFromAPI = (
  solicitacao: Solicitacao,
  source: SourceID,
): SolicitacaoState => {
  return {
    solicitacao,
    source,
    selected:
      solicitacao.solicitacao_pre_selecionada ||
      solicitacao.data_solicitacao !== null,
    selectable: true,
    requested: solicitacao.data_solicitacao !== null,
    loading: false,
    editing: false,
    saved: true,
  };
};

const SolicitacoesProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [{ passosExecutados }] = useExecucaoContext();

  const [solicitacoesState, setSolicitacoesState] = React.useState<
    SolicitacaoState[]
  >([]);
  const [sharedDevicesState, setSharedDevicesState] = React.useState<
    SharedDevicesState[]
  >([]);

  const solicitacoes = React.useMemo(
    () => solicitacoesState.map(item => item.solicitacao),
    [solicitacoesState],
  );

  const solicitados = React.useMemo(
    () =>
      solicitacoesState
        .filter(({ requested }) => requested)
        .map(item => item.solicitacao),
    [solicitacoesState],
  );

  const sourceByID = React.useMemo(() => {
    const map = new Map<number, PassoExecutado>();

    passosExecutados.forEach(passo => {
      map.set(passo.id, passo);
    });

    return map;
  }, [passosExecutados]);

  const previousSourceIDsRef = React.useRef<Set<SourceID>>(new Set());

  const filterByCriteria = React.useCallback(
    (criteria: Partial<SolicitacoesFilterCriteria>) => {
      return solicitacoesState.filter(item => {
        if (criteria.tipo && item.solicitacao.tipo !== criteria.tipo) {
          return false;
        }

        if (criteria.source && item.source !== criteria.source) {
          return false;
        }

        return true;
      });
    },
    [solicitacoesState],
  );

  const list = React.useCallback(
    (criteria: Partial<SolicitacoesFilterCriteria>) => {
      return filterByCriteria(criteria);
    },
    [filterByCriteria],
  );

  const itemsToRequest = React.useCallback(
    (criteria: Partial<SolicitacoesFilterCriteria>) =>
      filterByCriteria(criteria).filter(
        item => item.selected && item.saved && !item.requested,
      ),
    [filterByCriteria],
  );

  const hasItemsToRequest = React.useCallback(
    (criteria: Partial<SolicitacoesFilterCriteria>) => {
      return itemsToRequest(criteria).length > 0;
    },
    [itemsToRequest],
  );

  const updateItemsState = React.useCallback(
    (items: Solicitacao[], newState: Partial<SolicitacaoState>) => {
      setSolicitacoesState(prevState =>
        prevState.map(item => {
          if (items.some(i => i.id === item.solicitacao.id)) {
            return { ...item, ...newState };
          }
          return item;
        }),
      );
    },
    [],
  );

  const onAdd = React.useCallback(
    (
      pedido: Solicitacao['pedido'],
      tipo: Solicitacao['tipo'],
      addState: AddSolicitacaoState = {},
    ) => {
      // ID local aleatório e negativo: evita colisão com IDs do backend
      // (positivos) e com outros itens adicionados simultaneamente.
      const localId = -Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);

      const novaSolicitacao = {
        id: localId,
        tipo,
        pedido,
        categoria: 'Itens complementares',
        solicitacao_incluida: true,
      } as Solicitacao;

      setSolicitacoesState(prevState => [
        ...prevState,
        {
          solicitacao: novaSolicitacao,
          source: addState.source || null,
          selected: addState.selected || false,
          selectable: addState.selectable || true,
          loading: addState.loading || false,
          editing: addState.editing || false,
          requested: false,
          saved: false,
        },
      ]);
    },
    [],
  );

  const add = React.useCallback(
    async (
      solicitacao: Solicitacao,
      source: SourceID,
      resource?: fhir4.FhirResource,
    ) => {
      updateItemsState([solicitacao], { loading: true });

      const passo = sourceByID.get(source);
      if (passo) {
        const included = await SolicitacoesAPI.include({
          tipo: solicitacao.tipo,
          resource: resource || solicitacao.pedido,
          passo,
        });

        setSolicitacoesState(prevState =>
          prevState.map(item => {
            if (item.solicitacao.id === solicitacao.id) {
              return {
                ...item,
                solicitacao: included,
                loading: false,
                editing: false,
                saved: true,
              };
            }

            return item;
          }),
        );
      }
    },
    [sourceByID, updateItemsState],
  );

  const onEdit = React.useCallback(
    (solicitacao: Solicitacao) => {
      updateItemsState([solicitacao], { editing: true });
    },
    [updateItemsState],
  );

  const edit = React.useCallback(
    async (solicitacao: Solicitacao, data: fhir4.FhirResource) => {
      updateItemsState([solicitacao], { loading: true, editing: true });

      const { session_id = null } = solicitacao;

      const updated = await SolicitacoesAPI.update({
        id: solicitacao.id.toString(),
        tipo: solicitacao.tipo,
        resource: data,
        opts: {
          session_id,
        },
      });

      setSolicitacoesState(prevState =>
        prevState.map(item => {
          if (item.solicitacao.id !== updated.id) {
            return item;
          }

          return {
            ...item,
            solicitacao: updated,
            loading: false,
            editing: false,
            saved: true,
          };
        }),
      );
    },
    [updateItemsState],
  );

  const change = React.useCallback(
    (solicitacao: Solicitacao, newState: Partial<SolicitacaoUIState>) => {
      updateItemsState([solicitacao], newState);
    },
    [updateItemsState],
  );

  const changeSharedDevices = React.useCallback(
    ({ source, shared }: ChangeSharedDevices) => {
      setSharedDevicesState(prevState => {
        const existingIndex = prevState.findIndex(
          item => item.source === source,
        );

        if (existingIndex >= 0) {
          const updatedState = [...prevState];
          updatedState[existingIndex] = { source, shared };
          return updatedState;
        }

        return [...prevState, { source, shared }];
      });
    },
    [],
  );

  const deleteSolicitacao = React.useCallback(
    async (solicitacao: Solicitacao) => {
      setSolicitacoesState(prevState =>
        prevState.filter(item => item.solicitacao.id !== solicitacao.id),
      );
    },
    [],
  );

  const select = React.useCallback(
    (item: Solicitacao) => {
      updateItemsState([item], { selected: true });
    },
    [updateItemsState],
  );

  const selection = React.useCallback(
    (items: Solicitacao[]) => {
      // If any or none item selected, select all them, otherwise, deselect all
      const selectionIDs = items.map(item => item.id);
      const selectionState = solicitacoesState.filter(item =>
        selectionIDs.includes(item.solicitacao.id),
      );
      const allSelected = selectionState.every(item => item.selected);

      updateItemsState(items, { selected: !allSelected });
    },
    [solicitacoesState, updateItemsState],
  );

  const editItemsWithSharedDevices = React.useCallback(
    async (
      items: Solicitacao[],
      source: SourceID,
      uiState: Partial<SolicitacaoUIState> = {},
    ) => {
      const procedimentos = items.filter(
        item => item.tipo === 'procedimento',
      ) as Procedimento[];

      const materiais = sharedDevicesState.find(item => item.source === source);

      if (procedimentos.length > 0 && materiais) {
        const updated = await ProcedimentoService.updateFromSharedDevices(
          procedimentos,
          materiais.shared,
        );

        setSolicitacoesState(prevState =>
          prevState.map(item => {
            const updatedItem = updated.find(i => i.id === item.solicitacao.id);

            if (updatedItem) {
              return {
                ...item,
                solicitacao: updatedItem,
                ...uiState,
              };
            }

            return item;
          }),
        );
      }
    },
    [sharedDevicesState],
  );

  const request = React.useCallback(
    async (criteria: WithSourceCriteria) => {
      const items = itemsToRequest(criteria).map(item => item.solicitacao);

      if (items.length) {
        await editItemsWithSharedDevices(items, criteria.source);

        const source = sourceByID.get(criteria.source);
        if (source) {
          const all = await SolicitacoesAPI.requestAll(items, source);

          setSolicitacoesState(prevState =>
            prevState.map(item => {
              const apiItem = all.find(r => r.id === item.solicitacao.id);

              if (apiItem) {
                return solicitacaoStateFromAPI(apiItem, source.id);
              }

              return item;
            }),
          );
        }
      }
    },
    [itemsToRequest, editItemsWithSharedDevices, sourceByID],
  );

  const cancel = React.useCallback(
    async (criteria: WithSourceCriteria) => {
      // const { source } = criteria;
      // All items from source should comeback from original state

      const currentSource = sourceByID.get(criteria.source);
      if (currentSource) {
        const itemsToCancel = solicitacoesState
          .filter(item => item.source === criteria.source && item.requested)
          .map(item => item.solicitacao);

        const allFromAPI: Solicitacao[] = [];

        if (itemsToCancel.length > 0) {
          const response = await SolicitacoesAPI.cancelAll(
            itemsToCancel,
            currentSource,
          );

          allFromAPI.push(...response.results);
        }

        setSolicitacoesState(previous => {
          const allFromAPIById = new Map(
            allFromAPI.map(item => [item.id, item]),
          );

          return previous
            .map(item => {
              const apiItem = allFromAPIById.get(item.solicitacao.id);
              if (apiItem) {
                return solicitacaoStateFromAPI(apiItem, currentSource.id);
              }
              return item;
            })
            .filter(item => {
              if (!item.source) return true;
              const itemSource = sourceByID.get(item.source);
              return (
                !!itemSource &&
                itemSource.ordem_execucao <= currentSource.ordem_execucao
              );
            });
        });
      }
    },
    [solicitacoesState, sourceByID],
  );

  React.useEffect(() => {
    const sourceByIDEntries = Array.from(sourceByID.values());

    const newPassos = sourceByIDEntries.filter(
      passo => !previousSourceIDsRef.current.has(passo.id),
    );

    if (newPassos.length) {
      setSolicitacoesState(previousState => {
        const existingKeys = new Set(
          previousState.map(item => `${item.source}:${item.solicitacao.id}`),
        );

        const incomingState = newPassos
          .flatMap(passo =>
            passo.solicitacoes.map(solicitacao =>
              solicitacaoStateFromAPI(solicitacao, passo.id),
            ),
          )
          .filter(
            item => !existingKeys.has(`${item.source}:${item.solicitacao.id}`),
          );

        if (!incomingState.length) {
          return previousState;
        }

        return [...previousState, ...incomingState];
      });
    }

    previousSourceIDsRef.current = new Set(sourceByIDEntries.map(p => p.id));
  }, [sourceByID]);

  const contextValue = React.useMemo<ISolicitacaesContext>(
    () => ({
      solicitacoes,
      solicitados,
      list,
      hasItemsToRequest,
      onAdd,
      add,
      onEdit,
      edit,
      change,
      changeSharedDevices,
      destroy: deleteSolicitacao,
      select,
      selection,
      request,
      cancel,
    }),
    [
      solicitacoes,
      solicitados,
      list,
      hasItemsToRequest,
      onAdd,
      add,
      onEdit,
      edit,
      change,
      changeSharedDevices,
      deleteSolicitacao,
      select,
      selection,
      request,
      cancel,
    ],
  );

  return (
    <SolicitacoesContext.Provider value={contextValue}>
      {children}
    </SolicitacoesContext.Provider>
  );
};

export function useSolicitacoesContext(): ISolicitacaesContext {
  return React.useContext<ISolicitacaesContext>(SolicitacoesContext);
}

export const useSolicitacoes = useSolicitacoesContext;

export default SolicitacoesProvider;
